import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiGet, apiPost } from "../../lib/api";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../../lib/locales";
import defaultHeroBannerUrl from "../../assets/branding/HeroBannerPlayful.png";
import { ProgressBar } from "./booking/ProgressBar";
import { StepServices } from "./booking/StepServices";
import { StepTimeSlots } from "./booking/StepTimeSlots";
import { StepContact } from "./booking/StepContact";
import { BookingSuccess } from "./booking/BookingSuccess";

type CatalogAsset = {
  url: string;
  variants?: Record<string, string>;
  alt_text?: string | null;
  is_decorative?: boolean;
  mime_type?: string;
  type?: string;
  width?: number | null;
  height?: number | null;
  dominant_color?: string | null;
  blur_placeholder_data_url?: string | null;
};

type CompiledThemePayload = {
  schema_version?: number;
  content_json?: any;
  design_tokens_json?: any;
  pages_json?: { pages?: Array<{ id?: string; title?: string; blocks?: Array<any> }> };
  booking_theme_json?: any;
  asset_bindings_json?: any;
};

type Catalog = {
  tenant: { name: string; slug: string; default_locale: SupportedLocale };
  branch: { id: string; name: string; slug: string };
  staff: Array<{ id: string; full_name: string; photo_asset_id?: string | null }>;
  services: Array<{ id: string; name: string; duration_minutes: number; price_cents: number; photo_asset_id?: string | null }>;
  assets: Record<string, CatalogAsset>;
  branding: Record<string, string>;
  branding_v2?: {
    revision_id?: string | null;
    compiled?: {
      etag?: string | number | null;
      css_url?: string | null;
      json_url?: string | null;
      manifest_url?: string | null;
    };
    preview_active?: boolean;
  };
  snapshot?: {
    theme?: {
      components?: {
        header?: {
          variant?: "hero" | "compact";
          heroImageAssetId?: string;
          auxiliaryImageAssetIds?: string[];
          overlay?: { color?: string; opacity?: number };
          alignment?: "left" | "center";
          paddingY?: "sm" | "md" | "lg";
        };
        serviceCard?: {
          variant?: "list" | "grid" | "cardsWithImages";
          showDurationBadge?: boolean;
          showPrice?: boolean;
          imageMode?: "cover" | "contain";
        };
        staffSelector?: { variant?: "pills" | "cardsWithPhotos"; showPhotos?: boolean };
        slotChip?: { variant?: "outline" | "filled"; size?: "sm" | "md" };
        stepper?: { variant?: "numbers" | "dots"; showLabels?: boolean };
        form?: { inputVariant?: "outline" | "filled"; radius?: "sm" | "md" | "lg" };
      };
    };
    layout?: {
      sections?: Array<{ id: string; type: string; enabled: boolean; variant?: string }>;
    };
    content?: {
      header?: { title?: string; subtitle?: string };
      policies?: { beforeYouArrive?: string };
      footer?: { enabled?: boolean; text?: string; links?: Array<{ label: string; url: string }> };
    };
  };
  css_variables?: Record<string, string>;
  content: {
    hero_title?: string;
    hero_subtitle?: string;
    cta_label?: string;
    policy?: string;
    highlights?: string[];
  };
};

type Slot = { start_at: string; staff_id: string; score: number; factors: Record<string, number> };

type BookingResult = {
  appointment_id?: string;
  status?: string;
  start_at?: string;
  end_at?: string;
  service_id?: string;
  staff_id?: string;
};

type StartSessionResult = {
  session_token: string;
  expires_at: string;
  locale: SupportedLocale;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  phone_locked?: boolean;
};

type WaitlistSubmitPayload = {
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  notes?: string;
};

type TelegramWebAppHandle = {
  ready?: () => void;
  expand?: () => void;
  HapticFeedback?: {
    notificationOccurred?: (type: "success" | "error" | "warning") => void;
    impactOccurred?: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
  };
};

type WizardStep = "services" | "time" | "contact" | "success";
type AppLocale = SupportedLocale;

function normalizeLocale(value: string | null | undefined): AppLocale | null {
  const normalized = (value || "").toLowerCase().split("-")[0];
  if (SUPPORTED_LOCALES.includes(normalized as AppLocale)) return normalized as AppLocale;
  return null;
}

function hexToRgba(color: string, alpha: number) {
  const x = color.replace("#", "").trim();
  if (!(x.length === 3 || x.length === 6)) return `rgba(0,0,0,${alpha})`;
  const y = x.length === 3 ? x.split("").map((c) => `${c}${c}`).join("") : x;
  const n = parseInt(y, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatCountdown(secondsLeft: number) {
  const clamped = Math.max(0, secondsLeft);
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function compiledThemeToSnapshot(theme: CompiledThemePayload | null | undefined): Catalog["snapshot"] | undefined {
  if (!theme) return undefined;
  const pages = Array.isArray(theme.pages_json?.pages) ? theme.pages_json?.pages : [];
  const bookingPage = pages.find((p) => p?.id === "booking") || pages[0];
  const blocks = Array.isArray(bookingPage?.blocks) ? bookingPage?.blocks : [];
  const assetBindings = theme.asset_bindings_json || {};
  const heroMain = assetBindings?.["hero.main"];
  const heroAux = Array.isArray(assetBindings?.["hero.aux"]) ? assetBindings["hero.aux"] : [];
  return {
    theme: {
      components: {
        ...(theme.booking_theme_json || {}),
        header: {
          ...((theme.booking_theme_json || {}).header || {}),
          heroImageAssetId: typeof heroMain?.asset_id === "string" ? heroMain.asset_id : undefined,
          auxiliaryImageAssetIds: heroAux
            .map((x: any) => (typeof x?.asset_id === "string" ? x.asset_id : null))
            .filter(Boolean) as string[],
        },
      },
      tokens: theme.design_tokens_json || {},
    } as any,
    content: theme.content_json || {},
    layout: {
      sections: blocks.map((block: any, idx: number) => ({
        id: String(block?.id || `block-${idx + 1}`),
        type: String(block?.type || "section"),
        enabled: Boolean(block?.enabled ?? true),
        variant: block?.props?.variant,
        hiddenOnMobile: Boolean(block?.props?.hiddenOnMobile),
      })),
    },
  };
}

export function ClientBookingPage() {
  const { tenantSlug = "", branchSlug = "" } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { ...options, defaultValue: fallback });
    return translated === key ? fallback : translated;
  };
  const localeInitializedRef = useRef(false);
  const preselectionInitializedRef = useRef(false);
  const localeStorageKey = `client_locale:${tenantSlug}`;
  const previewToken = searchParams.get("preview") || searchParams.get("brandingPreview") || "";
  const [sessionToken, setSessionToken] = useState("");
  const [selectedLocale, setSelectedLocale] = useState<AppLocale>("en");
  const [step, setStep] = useState<WizardStep>("services");
  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [slot, setSlot] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [sessionExpiresAt, setSessionExpiresAt] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [phoneLocked, setPhoneLocked] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [formError, setFormError] = useState("");
  const [waitlistError, setWaitlistError] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const idempotencyKeyRef = useRef("");
  const telegramToken = searchParams.get("t") || "";
  const telegramWebApp = (
    window as unknown as {
      Telegram?: { WebApp?: TelegramWebAppHandle };
    }
  )?.Telegram?.WebApp;
  const isTelegramClientPath = location.pathname.startsWith("/tg/client/book/");
  const isTelegramClientMode = Boolean(
    isTelegramClientPath || telegramWebApp || searchParams.get("platform") === "telegram" || telegramToken
  );

  useEffect(() => {
    if (!isTelegramClientMode) return;
    try {
      telegramWebApp?.ready?.();
      telegramWebApp?.expand?.();
    } catch {
      // Best-effort only for Telegram WebApp shell.
    }
  }, [isTelegramClientMode, telegramWebApp]);

  const catalog = useQuery({
    queryKey: ["client-catalog", tenantSlug, branchSlug, previewToken],
    queryFn: () =>
      apiGet<Catalog>(
        `/v3/public/catalog/${tenantSlug}/${branchSlug}${previewToken ? `?preview=${encodeURIComponent(previewToken)}` : ""}`,
        { auth: "none" }
      ),
  });

  const compiledTheme = useQuery({
    queryKey: ["client-compiled-theme", catalog.data?.branding_v2?.compiled?.json_url],
    queryFn: () => apiGet<CompiledThemePayload>(String(catalog.data?.branding_v2?.compiled?.json_url), { auth: "none" }),
    enabled: !!catalog.data?.branding_v2?.compiled?.json_url,
    staleTime: 60_000,
  });

  useEffect(() => {
    const href = catalog.data?.branding_v2?.compiled?.css_url;
    if (!href) return;
    const id = "brand-compiled-theme-css";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = href;
    return () => {
      // Keep the latest loaded theme stylesheet between route re-renders.
    };
  }, [catalog.data?.branding_v2?.compiled?.css_url]);

  // Auto-start session
  const start = useMutation({
    mutationFn: () =>
      apiPost<StartSessionResult>(
        "/v3/public/session",
        {
          tenant_slug: tenantSlug,
          branch_slug: branchSlug,
          telegram_token: telegramToken || undefined,
        },
        { auth: "none" }
      ),
    onSuccess: (data) => {
      setSessionToken(data.session_token);
      setSessionExpiresAt(data.expires_at);
      if (data.customer_name) setCustomerName((prev) => prev || data.customer_name || "");
      if (data.customer_phone) setCustomerPhone((prev) => prev || data.customer_phone || "");
      if (data.customer_email) setCustomerEmail((prev) => prev || data.customer_email || "");
      setPhoneLocked(Boolean(data.phone_locked));
    },
  });

  useEffect(() => {
    if (!sessionToken && !start.isPending && !start.isSuccess) {
      start.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlLocale = normalizeLocale(searchParams.get("lang"));
    const storedLocale = normalizeLocale(localStorage.getItem(localeStorageKey));
    const sessionLocale = normalizeLocale(start.data?.locale);
    const tenantLocale = normalizeLocale(catalog.data?.tenant.default_locale);
    if (!localeInitializedRef.current || urlLocale) {
      const nextLocale = urlLocale || storedLocale || sessionLocale || tenantLocale || "en";
      setSelectedLocale(nextLocale);
      localeInitializedRef.current = true;
    }
  }, [searchParams, localeStorageKey, start.data?.locale, catalog.data?.tenant.default_locale]);

  useEffect(() => {
    i18n.changeLanguage(selectedLocale);
    if (tenantSlug) {
      localStorage.setItem(localeStorageKey, selectedLocale);
    }
  }, [selectedLocale, i18n, tenantSlug, localeStorageKey]);

  useEffect(() => {
    if (!catalog.data || preselectionInitializedRef.current) return;
    const serviceParam = searchParams.get("service_id");
    const dayParam = searchParams.get("day");
    const staffParam = searchParams.get("staff_id");

    let hasServicePreselection = false;
    if (serviceParam && catalog.data.services.some((item) => item.id === serviceParam)) {
      setServiceId((current) => current || serviceParam);
      hasServicePreselection = true;
    }
    if (dayParam && /^\d{4}-\d{2}-\d{2}$/.test(dayParam)) {
      setDay(dayParam);
    }
    if (staffParam && catalog.data.staff.some((item) => item.id === staffParam)) {
      setStaffId(staffParam);
    }
    if (hasServicePreselection) {
      setStep("time");
    }
    preselectionInitializedRef.current = true;
  }, [searchParams, catalog.data]);

  const availabilityPath = useMemo(() => {
    if (!sessionToken || !serviceId) return "";
    return `/v3/public/smart-availability?session_token=${encodeURIComponent(sessionToken)}&service_id=${encodeURIComponent(serviceId)}&day=${encodeURIComponent(day)}${
      staffId ? `&staff_id=${encodeURIComponent(staffId)}` : ""
    }`;
  }, [sessionToken, serviceId, day, staffId]);

  const availability = useQuery({
    queryKey: ["client-slots", availabilityPath],
    queryFn: () => apiGet<{ items: Slot[] }>(availabilityPath, { auth: "none" }),
    enabled: !!availabilityPath,
  });

  const book = useMutation({
    mutationFn: () => {
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = createIdempotencyKey();
      }
      return apiPost<BookingResult>(
        isTelegramClientMode ? "/v3/public/telegram/book" : "/v3/public/book",
        {
          session_token: sessionToken,
          service_id: serviceId,
          staff_id: staffId,
          start_at: slot,
          locale: selectedLocale,
          customer_name: customerName,
          customer_phone: customerPhone,
          customer_email: customerEmail,
        },
        {
          auth: "none",
          headers: { "Idempotency-Key": idempotencyKeyRef.current },
        }
      );
    },
    onSuccess: (data) => {
      setFormError("");
      setBookingResult(data);
      setStep("success");
      idempotencyKeyRef.current = "";
      try {
        telegramWebApp?.HapticFeedback?.notificationOccurred?.("success");
      } catch {
        // Ignore non-Telegram environments.
      }
    },
    onError: (err) => {
      setFormError(String(err));
      try {
        telegramWebApp?.HapticFeedback?.notificationOccurred?.("error");
      } catch {
        // Ignore non-Telegram environments.
      }
    },
  });

  const waitlist = useMutation({
    mutationFn: (payload: WaitlistSubmitPayload) =>
      apiPost(
        "/v3/public/waitlist",
        {
          session_token: sessionToken,
          service_id: serviceId,
          preferred_staff_id: staffId || undefined,
          desired_day: day,
          customer_name: payload.customer_name,
          customer_phone: payload.customer_phone,
          customer_email: payload.customer_email,
          notes: payload.notes,
          locale: selectedLocale,
        },
        { auth: "none" }
      ),
    onSuccess: () => {
      setWaitlistError("");
      setWaitlistSubmitted(true);
    },
    onError: (err) => {
      setWaitlistError(String(err));
      setWaitlistSubmitted(false);
    },
  });

  useEffect(() => {
    setSlot("");
    setFormError("");
  }, [serviceId, day]);

  useEffect(() => {
    setWaitlistError("");
    setWaitlistSubmitted(false);
  }, [serviceId, day, staffId]);

  useEffect(() => {
    idempotencyKeyRef.current = "";
  }, [sessionToken, serviceId, staffId, slot, customerName, customerPhone, customerEmail, isTelegramClientMode]);

  useEffect(() => {
    if (!sessionExpiresAt) return;
    const tick = () => {
      const diff = Math.floor((new Date(sessionExpiresAt).getTime() - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, diff));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [sessionExpiresAt]);

  // Helpers
  const staffNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of catalog.data?.staff ?? []) {
      m.set(s.id, s.full_name);
    }
    return m;
  }, [catalog.data?.staff]);
  const staffByIdMap = useMemo(() => {
    const m = new Map<string, { id: string; full_name: string; photo_asset_id?: string | null }>();
    for (const s of catalog.data?.staff ?? []) {
      m.set(s.id, s);
    }
    return m;
  }, [catalog.data?.staff]);

  const selectedService = (catalog.data?.services ?? []).find((s) => s.id === serviceId);
  const slots = availability.data?.items ?? [];

  useEffect(() => {
    if (slots.length > 0 && waitlistSubmitted) {
      setWaitlistSubmitted(false);
      setWaitlistError("");
    }
  }, [slots.length, waitlistSubmitted]);

  const handleConfirm = () => {
    if (secondsLeft <= 0) {
      setFormError(tr("client.booking.sessionExpiredError", "Your booking session expired. Please refresh and start again."));
      return;
    }
    if (!phoneLocked && !customerName.trim()) {
      setFormError(tr("client.booking.nameRequired", "Please enter your name."));
      return;
    }
    setFormError("");
    book.mutate();
  };

  const handleWaitlistSubmit = (payload: WaitlistSubmitPayload) => {
    if (!serviceId || !sessionToken) return;
    if (payload.customer_name) setCustomerName(payload.customer_name);
    if (!phoneLocked && payload.customer_phone) setCustomerPhone(payload.customer_phone);
    if (payload.customer_email) setCustomerEmail(payload.customer_email);
    setWaitlistError("");
    waitlist.mutate(payload);
  };

  const handleBookAnother = () => {
    setServiceId("");
    setStaffId("");
    setSlot("");
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setBookingResult(null);
    setFormError("");
    setWaitlistError("");
    setWaitlistSubmitted(false);
    setStep("services");
  };

  const theme = catalog.data?.branding ?? {};
  const compiledSnapshot = compiledTheme.data ? compiledThemeToSnapshot(compiledTheme.data) : undefined;
  const snapshot = (compiledSnapshot || catalog.data?.snapshot || {}) as any;
  const content = catalog.data?.content ?? {};
  const assets = catalog.data?.assets ?? {};
  const selectedStaff = staffId ? staffByIdMap.get(staffId) : undefined;
  const selectedStaffAsset = selectedStaff?.photo_asset_id ? assets[selectedStaff.photo_asset_id] : undefined;
  const components = snapshot.theme?.components ?? {};
  const headerFromSnapshot = snapshot.content?.header;
  const policyFromSnapshot = snapshot.content?.policies?.beforeYouArrive;
  const footerFromSnapshot = snapshot.content?.footer;
  const localizedStrings =
    (snapshot.content?.i18n?.strings && typeof snapshot.content?.i18n?.strings === "object"
      ? snapshot.content.i18n.strings
      : snapshot.content?.strings && typeof snapshot.content?.strings === "object"
        ? snapshot.content.strings
        : {}) || {};
  const localizedText = (key: string, fallback?: string) => {
    const entry = localizedStrings[key];
    if (entry && typeof entry === "object") {
      const current = entry[selectedLocale];
      if (typeof current === "string" && current.trim()) return current;
      const base = entry.en;
      if (typeof base === "string" && base.trim()) return base;
    }
    return fallback;
  };
  const layoutSections = (snapshot.layout?.sections ?? []) as Array<{ id: string; type: string; enabled: boolean; hiddenOnMobile?: boolean }>;
  const enabledTypes = new Set(layoutSections.filter((section: { enabled: boolean }) => section.enabled).map((section: { type: string }) => section.type));
  const hasSectionConfig = layoutSections.length > 0;
  const showHowItWorks = !hasSectionConfig || enabledTypes.has("howItWorks");
  const showPolicies = !hasSectionConfig || enabledTypes.has("policies");
  const showFooter = (!hasSectionConfig || enabledTypes.has("footer")) && (footerFromSnapshot?.enabled ?? true);

  const headerStyle = components.header ?? {};
  const serviceCardStyle = components.serviceCard ?? {};
  const staffSelectorStyle = components.staffSelector ?? {};
  const slotChipStyle = components.slotChip ?? {};
  const stepperStyle = components.stepper ?? {};
  const formStyle = components.form ?? {};

  const mainHeroAsset = headerStyle.heroImageAssetId ? assets[headerStyle.heroImageAssetId] : undefined;
  const heroVariantPreferred =
    typeof window !== "undefined" && window.innerWidth < 640
      ? mainHeroAsset?.variants?.hero_mobile_16x9
      : mainHeroAsset?.variants?.hero_desktop_4x1;
  const heroBannerUrl = mainHeroAsset?.variants?.web_1600 || heroVariantPreferred || mainHeroAsset?.url || defaultHeroBannerUrl;
  const auxHeroAssets = ((headerStyle.auxiliaryImageAssetIds ?? []) as string[]).map((id: string) => assets[id]).filter(Boolean) as CatalogAsset[];
  const overlayColor = headerStyle.overlay?.color || "#000000";
  const overlayOpacity = Number.isFinite(headerStyle.overlay?.opacity) ? Math.min(Math.max(Number(headerStyle.overlay?.opacity), 0), 1) : 0.35;
  const headerPaddingClass =
    headerStyle.paddingY === "sm"
      ? "py-4"
      : headerStyle.paddingY === "md"
        ? "py-6"
        : "py-8";
  const headerAlignmentClass = headerStyle.alignment === "center" ? "text-center" : "text-left";
  const heroStyle = {
    backgroundImage: `linear-gradient(180deg, ${hexToRgba(overlayColor, overlayOpacity)}, ${hexToRgba(overlayColor, overlayOpacity)}), url(${heroBannerUrl})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  } as CSSProperties;

  const cssVars = catalog.data?.css_variables ?? {};
  const styleVars = {
    ["--client-primary" as string]: cssVars["--c-primary"] || theme.primary || "#0f766e",
    ["--client-accent" as string]: cssVars["--c-accent"] || theme.accent || "#f59e0b",
    ["--client-bg" as string]: cssVars["--c-bg"] || "#f8fafc",
    ["--client-surface" as string]: cssVars["--c-surface"] || "#ffffff",
    ["--client-border" as string]: cssVars["--c-border"] || "#dbe3e7",
    ["--client-text" as string]: cssVars["--c-text"] || "#0f172a",
    ["--client-text-muted" as string]: cssVars["--c-text-2"] || "#475569",
  } as CSSProperties;

  return (
    <div className="client-container" style={styleVars}>
      {/* Hero */}
      <section className={`client-hero ${headerPaddingClass} ${headerAlignmentClass}`} style={heroStyle}>
        {isTelegramClientMode && (
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs text-white">
            <span>Telegram Web App</span>
            <span className="h-1 w-1 rounded-full bg-white/70" />
            <span>Client mode</span>
          </div>
        )}
        <div className="flex justify-end">
          <select
            className="input w-full max-w-[180px] bg-white/90"
            aria-label="Booking page language"
            value={selectedLocale}
            onChange={(e) => setSelectedLocale(e.target.value as AppLocale)}
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {tr(`locale.${locale}`, locale.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
        <h1>
          {localizedText("hero.title", headerFromSnapshot?.title) ||
            content.hero_title ||
            catalog.data?.tenant.name ||
            tr("client.booking.heroTitle", "Book your visit")}
        </h1>
        <p>
          {localizedText("hero.subtitle", headerFromSnapshot?.subtitle) ||
            content.hero_subtitle ||
            tr("client.booking.heroSubtitle", "Select your service and preferred time.")}
        </p>
        {auxHeroAssets.length > 0 && (
          <div className={`mt-4 grid grid-cols-3 gap-2 ${headerAlignmentClass === "text-center" ? "mx-auto max-w-sm" : "max-w-md"}`}>
            {auxHeroAssets.slice(0, 3).map((asset: CatalogAsset) => (
              <img key={asset.url} src={asset.url} alt={asset.alt_text || tr("client.booking.galleryAlt", "Gallery")} className="h-16 w-full rounded-lg object-cover border border-white/30" />
            ))}
          </div>
        )}
      </section>

      {showHowItWorks && (
        <section className="panel p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{tr("client.booking.howItWorks", "How it works")}</h2>
          <p className="text-sm mt-2 text-slate-600">{tr("client.booking.howItWorksBody", "Choose service, pick a specialist and confirm your appointment in less than a minute.")}</p>
        </section>
      )}

      {/* Progress */}
      {step !== "success" && <ProgressBar current={step} styleConfig={stepperStyle} />}

      {step !== "success" && sessionToken ? (
        <section className="panel p-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">{tr("client.booking.sessionTimer", "Session reservation timer")}</p>
          <span className="countdown-chip" data-testid="session-countdown">
            {secondsLeft > 0
              ? tr("client.booking.sessionLeft", "{{time}} left", { time: formatCountdown(secondsLeft) })
              : tr("client.booking.sessionExpired", "expired")}
          </span>
        </section>
      ) : null}

      {isTelegramClientMode && step !== "success" ? (
        <section className="panel p-3">
          <p className="text-xs text-slate-600">
            Telegram client app mode is enabled. Booking confirmations will be created with Telegram channel attribution.
          </p>
        </section>
      ) : null}

      {/* Loading skeleton */}
      {catalog.isLoading && (
        <section className="panel p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-2xl" />
            ))}
          </div>
        </section>
      )}

      {/* Wizard steps */}
      {catalog.isSuccess && (
        <section className="panel p-4 sm:p-5">
          {step === "services" && (
            <StepServices
              services={catalog.data.services}
              assets={assets}
              styleConfig={serviceCardStyle}
              selectedId={serviceId}
              onSelect={(id) => {
                setServiceId(id);
                setFormError("");
              }}
              onContinue={() => setStep("time")}
            />
          )}

          {step === "time" && (
            <StepTimeSlots
              slots={slots}
              staff={catalog.data.staff}
              assets={assets}
              staffSelectorStyle={staffSelectorStyle}
              slotChipStyle={slotChipStyle}
              selectedStaffId={staffId}
              onStaffChange={(id) => {
                setStaffId(id);
                setSlot("");
              }}
              day={day}
              onDayChange={(d) => {
                setDay(d);
                setSlot("");
              }}
              selectedSlot={slot}
              onSlotSelect={(startAt, sid) => {
                setSlot(startAt);
                setStaffId(sid);
              }}
              onContinue={() => setStep("contact")}
              isLoading={availability.isLoading}
              serviceName={selectedService?.name || ""}
              staffNameMap={staffNameMap}
              locale={selectedLocale}
              waitlist={{
                isSubmitting: waitlist.isPending,
                isSubmitted: waitlistSubmitted,
                error: waitlistError,
                initialName: customerName,
                initialPhone: customerPhone,
                initialEmail: customerEmail,
                phoneLocked,
                onSubmit: handleWaitlistSubmit,
              }}
            />
          )}

          {step === "contact" && (
            <StepContact
              serviceName={selectedService?.name || ""}
              dateTime={slot}
              staffName={staffNameMap.get(staffId) || ""}
              staffPhotoUrl={
                selectedStaffAsset?.variants?.card_4x3 ||
                selectedStaffAsset?.variants?.card_1x1 ||
                selectedStaffAsset?.variants?.web_1600 ||
                selectedStaffAsset?.url
              }
              staffPhotoAlt={selectedStaffAsset?.alt_text || (selectedStaff?.full_name ? `${selectedStaff.full_name} photo` : undefined)}
              customerName={customerName}
              customerPhone={customerPhone}
              customerEmail={customerEmail}
              onNameChange={setCustomerName}
              onPhoneChange={setCustomerPhone}
              onEmailChange={setCustomerEmail}
              nameRequired={!phoneLocked}
              phoneDisabled={phoneLocked}
              onConfirm={handleConfirm}
              isPending={book.isPending}
              error={formError}
              policy={showPolicies ? localizedText("policy.text", policyFromSnapshot) || content.policy : undefined}
              highlights={showPolicies ? content.highlights : undefined}
              formStyle={formStyle}
            />
          )}

          {step === "success" && (
            <BookingSuccess
              serviceName={selectedService?.name || ""}
              dateTime={slot}
              endDateTime={bookingResult?.end_at}
              staffName={staffNameMap.get(staffId) || ""}
              status={bookingResult?.status}
              branchName={catalog.data?.branch.name}
              onBookAnother={handleBookAnother}
            />
          )}
        </section>
      )}

      {showFooter && (
        <section className="panel p-4 text-sm text-slate-600">
          <p>{localizedText("footer.text", footerFromSnapshot?.text) || tr("client.booking.footerThanks", "Thank you for booking with us.")}</p>
          {(footerFromSnapshot?.links ?? []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-3">
              {(footerFromSnapshot?.links ?? []).map((link: { label: string; url: string }) => (
                <a key={link.url} href={link.url} target="_blank" rel="noreferrer" className="underline">
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

